import {
  ColumnStore,
  ColumnStoreConfig,
  GanttConfig,
  GridColumnConfig,
  ProjectModelConfig,
  TaskModel
} from '@bryntum/gantt';
import {
  ANONYMOUS_CLIENT_NAME,
  DEAL_SIZE_TREND_CHARACTERISTICS,
  DYNAMIC_COLUMNS,
  PURCHASE_CHARACTERISTICS_VALUE,
  PURCHASE_CHARACTERISTICS_THREE_COLORS,
  RENEWAL_RECORD_TYPE,
  PURCHASE_CHARACTERISTICS_EIGHT_COLORS
} from 'shared/constants';

import { Avatar, Badge, Button, Col, Row, Space, Tooltip } from 'antd';
import { ACLCheck, FieldSorter } from 'shared/Helpers';
import { RenewalAlertsModal } from 'components/renewals/RenewalAlertsModal';
import moment from 'moment-timezone';
import { useRecoilValue } from 'recoil';
import { profileSwitchState } from 'recoil/atoms';
import React from 'react';
import {
  BankFilled,
  BellFilled,
  BellOutlined,
  BuildFilled,
  CheckCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  StarFilled
} from '@ant-design/icons';
import { useDealHelper } from 'hooks/useDealHelper';
import { Link } from 'react-router-dom';
import SupplierDynamicsPopover from '../../atoms/SupplierDynamicsPopover';
import RenewalProposalsPopover from './RenewalProposalsPopover';
import { GanttChartMode } from 'sb/shared/constants';
import { taskRenderer } from 'shared/BryntumGantt';
import { Pebble } from 'components/shared';
import { CalendarReportsCell } from './CalendarReportsCell';

class MyTaskModel extends TaskModel {
  static get fields() {
    return [
      { name: 'name', type: 'string' },
      { name: 'categories' },
      { name: 'tcv', type: 'number' },
      { name: 'endDate' },
      { name: 'fmvReportLink' },
      { name: 'primaryContact' },
      { name: 'status' },
      { name: 'reseller' },
      { name: 'tags' },
      { name: 'proposals' },
      { name: 'deliverablesCount', type: 'number' },
      { name: 'fiscal_year_end' },
      { name: 'vendorComplexity' },
      { name: 'pricingFlexibility' },
      { name: 'dealSizeTrend' }
    ];
  }
}

export enum ContractStatus {
  CLOSED = 'Closed',
  FINDINGS_PROVIDED = 'Findings Provided',
  ON_HOLD = 'On Hold',
  OPEN = 'Open',
  WORKING = 'Working',
  SUBMITTED = 'Submitted',
  CANCELLED = 'Cancelled',
  PENDING_ASSIGNMENT = 'Pending Assignment',
  IN_PROGRESS = 'In Progress',
  NEGOTIATION = 'Negotiation',
  RESEARCH = 'Research',
  CALENDAR = 'Calendar',
  PENDING_OUTCOME = 'Pending Outcome'
}

const ganttChartColumnConfig = {
  [GanttChartMode.OWN_CALENDER]: [
    'col-name',
    'col-categories',
    // 'col-tcvAgg',
    'col-renewal',
    'col-noticePeriod',
    'col-alert',
    'col-fmvReportLink',
    'col-reports',
    'col-primaryContact',
    'col-status',
    'col-fye',
    'col-vendorComplexity',
    'col-pricingFlexibility',
    'col-dealSizeTrend',
    'col-reseller',
    'col-proposals',
    'col-tags'
  ],
  [GanttChartMode.PORTFOLIO]: [
    'col-name',
    'col-categories',
    // 'col-tcvAgg',
    'col-renewal',
    'col-noticePeriod',
    'col-fmvReportLink',
    'col-reports',
    'col-fye',
    'col-vendorComplexity',
    'col-pricingFlexibility',
    'col-dealSizeTrend',
    'col-reseller',
    'col-proposals',
    'col-tags'
  ]
};

/**
 * Resolves which Bryntum column ids a user may see for a Gantt mode.
 *
 * Two columns belong to the v2 (deal-table) calendar flow and are early-access only:
 * `col-proposals` and `col-reports`. `col-reports` **replaces** the legacy
 * `col-fmvReportLink` ("Vantage Report") column — the deal layer never populates
 * `fmvReportLink`, so exactly one of the two is ever allowed.
 *
 * `col-dealSizeTrend` is v1-only for the opposite reason (VE-2225): its calculated trend is
 * misleading on v2, where Proposal-based contracts invalidate the assumptions it is built on,
 * so it is gated `!hasEarlyAccess` until the calculation is redesigned. It stays fully wired
 * for v1, so re-enabling it on v2 means dropping it from this one gate.
 *
 * `col-alert` is v1-only on the same footing (VE-2471): renewal alerts are keyed on the
 * legacy `contract.id`, which a deal-sourced row does not have — its record id is the deal
 * uuid — so the bell issued a request the contract endpoint rejects with a 400. It is
 * withheld from v2 until alerts are keyed off the deal layer.
 *
 * Pure and exported so the gating is unit-testable without rendering the Gantt.
 * @param {GanttChartMode} mode - The Gantt mode whose column set is being resolved.
 * @param {boolean} hasEarlyAccess - Whether the user holds `read:early_access`.
 * @returns {string[]} The allowed column ids, in the configured order.
 */
export const resolveAllowedColumnIds = (mode: GanttChartMode, hasEarlyAccess: boolean): string[] =>
  ganttChartColumnConfig[mode].filter((columnId) => {
    if (columnId === 'col-proposals' || columnId === 'col-reports') return hasEarlyAccess;
    if (columnId === 'col-fmvReportLink' || columnId === 'col-dealSizeTrend' || columnId === 'col-alert')
      return !hasEarlyAccess;
    return true;
  });

/**
 * The single source of truth for left-to-right Gantt column render order.
 *
 * Must mirror the PHYSICAL definition order of the column objects inside the
 * `ganttChartColumns` array in `useGanttConfig` — NOT the order of either array in
 * `ganttChartColumnConfig` (those are allow-lists only; see `resolveAllowedColumnIds`).
 * Render order is derived from this list rather than from the `ganttChartColumns` array's
 * own position so that moving a column-definition object in the source can no longer
 * silently reorder the rendered Gantt: any drift between this list and the physical
 * definition order is now a data-driven fact, not an accident of file layout.
 */
export const GANTT_COLUMN_ORDER: string[] = [
  'col-name',
  'col-reseller',
  'col-proposals',
  'col-tcvAgg',
  'col-renewal',
  'col-noticePeriod',
  'col-fmvReportLink',
  'col-reports',
  'col-primaryContact',
  'col-status',
  'col-categories',
  'col-tags',
  'col-alert',
  'col-fye',
  'col-vendorComplexity',
  'col-pricingFlexibility',
  'col-dealSizeTrend'
];

/**
 * Resolves the ids of the columns the Gantt actually renders, left-to-right.
 *
 * Composes the two pure building blocks above: `resolveAllowedColumnIds` decides *which*
 * columns a user may see for a mode, `GANTT_COLUMN_ORDER` decides *where* each one sits.
 * `useGanttConfig` consumes this directly (mapping each id to its column definition), so it is
 * the real rendered sequence — exported so unit tests can assert on that sequence without
 * mounting Bryntum, rather than re-implementing the composition and drifting from it.
 * @param {GanttChartMode} mode - The Gantt mode whose columns are being resolved.
 * @param {boolean} hasEarlyAccess - Whether the user holds `read:early_access`.
 * @returns {string[]} The rendered column ids, in render order.
 */
export const resolveRenderedColumnIds = (mode: GanttChartMode, hasEarlyAccess: boolean): string[] => {
  const allowed = resolveAllowedColumnIds(mode, hasEarlyAccess);
  return GANTT_COLUMN_ORDER.filter((columnId) => allowed.includes(columnId));
};

const { ROOT, SUPPLIER, SUPPLIER_DEAL, DEAL, TASK, CHILD_ORG } = RENEWAL_RECORD_TYPE;

const useGanttConfig = (mode: GanttChartMode): Partial<GanttConfig> => {
  const profileSwitch = useRecoilValue(profileSwitchState);
  const { handleEditDealTask } = useDealHelper();

  /**
   * Edit a task in a deal
   * @param {number} dealId - The ID of the deal
   * @param {number} taskId - The ID of the task to be edited
   * @param {object} data - The data for the edited task
   * @param {string} data.name - The name of the task
   * @param {string} data.note - The note for the task
   * @param {Array} data.assignment - The assignments for the task
   * @param {string} data.startDate - The start date of the task (formatted as 'MM/DD/YYYY')
   * @param {string} data.endDate - The end date of the task (formatted as 'MM/DD/YYYY')
   * @returns {void}
   */
  const handleEditTask = (dealId: number, taskId: number, data: any): void => {
    const task = {
      name: data.name,
      note: data.note,
      assignments:
        data?.assignment?.map((a: any) => {
          return a.users.auth0_user_id;
        }) || [],
      startDate: moment(data.startDate).format('MM/DD/YYYY'),
      endDate: moment(data.endDate).format('MM/DD/YYYY')
    };
    handleEditDealTask(dealId, taskId, task);
  };

  /**
   * Renders a tooltip for client contact with a title and a list of names.
   * @param {string} title - The title of the tooltip.
   * @param {Array<string>} names - An array of names to be displayed in the tooltip.
   * @returns {React.ReactElement} - The rendered tooltip component.
   */
  const renderToolTipForClientContact = (title: string, names: Array<string>): React.ReactElement => (
    <div style={{ textAlign: 'center' }}>
      <span className="deal-owner-text">
        {title}
        <hr className="hr-no-space" />
      </span>
      {names.map((name: string, index: number) => (
        <span className="deal-owner-names" key={index}>
          {name}
          <br />
        </span>
      ))}
    </div>
  );

  /**
   * Function to render client contact avatars.
   * @param {string} fullName - The full name of the contact.
   * @param {string} styleClass - The style class to be applied to the avatar.
   * @returns {React.ReactElement} - The avatar component.
   */
  const renderClientContactAvatars = (fullName: string, styleClass: string): React.ReactElement => {
    const [firstNameInitial, lastNameInitial] = fullName.split(' ').map((name) => name.charAt(0));
    const initials = (
      <>
        <div className={`initials ${styleClass}`}>
          {firstNameInitial}
          {lastNameInitial ? lastNameInitial : null}
        </div>
      </>
    );
    return (
      <Avatar size={30} className={styleClass}>
        {initials}
      </Avatar>
    );
  };

  /**
   * Retrieves the color code for the specified deal size trend value.
   * @param {number} value - The deal size trend value.
   * @returns {string} The color code associated with the deal size trend value.
   */
  const getDealSizeTrendColor = (value: number): string => {
    let index;
    if (value <= -20) {
      index = 0;
    } else if (value >= 20) {
      index = 7;
    } else {
      value = value + 20;
      index = Math.floor(value / 5);
    }
    return PURCHASE_CHARACTERISTICS_EIGHT_COLORS[index];
  };

  /**
   * Retrieves the supplier dynamics object from a given record based on the provided property.
   * @param {any} record - The record object from which to retrieve the supplier dynamics.
   * @param {string} prop - The property value to match with the dealDynamic.key property of each supplier dynamics object.
   * @returns {any} - The matching supplier dynamics object, if found. Otherwise, returns undefined.
   */
  const getSupplierDynamicsObj = (record: any, prop: string): any => {
    const supplierDynamics = record.data?.supplierDynamics;
    return supplierDynamics?.find((item: any) => item?.dealDynamic?.key === prop);
  };

  const getNameValue = (record: any) => {
    let name = '';
    if (record.data.recordType === ROOT && profileSwitch?.anonymous) {
      name = ANONYMOUS_CLIENT_NAME;
    } else if (record.data.recordType === ROOT && !profileSwitch?.anonymous) {
      name = record.data.name;
    } else if (record.data.recordType === SUPPLIER_DEAL && record.data.status !== 'Calendar') {
      name = `${record.data.orgName || ''}: ${record.data.subject || ''}`;
    } else if (record.data.recordType === SUPPLIER_DEAL && record.data.status === 'Calendar') {
      name = `${record.data.orgName || ''}: ${record.data.subject || '--'}`;
    } else if (record.data.recordType === SUPPLIER) {
      name = record.data.name;
    } else if (record.data.recordType === DEAL && record.data.status !== 'Calendar') {
      name = `${record.data.subject || record.data.name}`;
    } else if (record.data.recordType === DEAL && record.data.status === 'Calendar') {
      name = `${record.data.subject || '--'}`;
    }

    return name;
  };

  const getPrimaryContacts = (record: any) => {
    if (record.data && (record.data.recordType === SUPPLIER_DEAL || record.data.recordType === DEAL)) {
      return record.data.clientContact?.Name ? [record.data.clientContact?.Name] : [];
    } else if (record.data && record.data.recordType === TASK) {
      const names = record.data.assignment?.map((item: any) => item.users.name);
      return names.length > 0 ? names.filter((name: string) => !!name).sort() : [];
    }
    return [];
  };

  return React.useMemo(() => {
    const ganttChartColumns: ColumnStore | Partial<GridColumnConfig>[] | Partial<ColumnStoreConfig> = [
      {
        id: 'col-name',
        type: 'name',
        field: 'name',
        // Bryntum's NameColumn defaults its header to "Name". VE-2264 standardizes the
        // deal-identifying label on "Deal Title" across Vantage, so set it explicitly —
        // this also drives the label in Bryntum's built-in column picker, which reads `text`.
        text: 'Deal Title',
        width: 400,
        // autoWidth: true,
        htmlEncode: false,
        renderer: ({ record, value }: any) => {
          // Check if record.data exists to prevent undefined access errors
          if (!record?.data) {
            return value;
          }

          if (record.data.recordType === ROOT) {
            return profileSwitch?.anonymous ? (
              <strong>{ANONYMOUS_CLIENT_NAME}</strong>
            ) : (
              <Space>
                {value}
                <Tooltip title="Count of contracts">
                  <Badge count={record.data.contractsCount} showZero={true} overflowCount={99999} />
                </Tooltip>
              </Space>
            );
          } else if (record.data.recordType === SUPPLIER_DEAL) {
            return (
              <>
                <div className="flex items-center deal-row">
                  {record.data.status !== 'Calendar' ? (
                    <div className="flex items-center">
                      <FileTextOutlined className="mr-2 opacity-70" />
                      <strong className="flex-1 pr-6 whitespace-normal line-clamp-2">
                        <Link to={`/deal_details/${record.data.id}`}>
                          {record.data.orgName}: {record.data.subject}
                        </Link>
                      </strong>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Tooltip title="Calendar Deal">
                        <StarFilled className="mr-2 opacity-70" />
                      </Tooltip>
                      <strong className="flex-1 pr-6 whitespace-normal line-clamp-2">
                        <Link to={`/calendar_deals/${record.data.id}`}>
                          {record.data.orgName}: {record.data.subject || '--'}
                        </Link>
                      </strong>
                    </div>
                  )}
                </div>
              </>
            );
          } else if (record.data.recordType === CHILD_ORG) {
            return (
              <Space size={3}>
                <BuildFilled className="opacity-70" />
                <strong>{value}</strong>
                <Tooltip title="Count of deals">
                  <Badge count={record.data.contractsCount} showZero={true} overflowCount={99999} />
                </Tooltip>
              </Space>
            );
          } else if (record.data.recordType === SUPPLIER) {
            return (
              <Space size={3}>
                <BankFilled className="opacity-70" /> <strong>{value}</strong>
                <Tooltip title="Count of deals">
                  <Badge count={record.data.contractsCount} showZero={true} overflowCount={99999} />
                </Tooltip>
              </Space>
            );
          } else if (record.data.recordType === DEAL) {
            return (
              <div className="flex items-center deal-row">
                {record.data.status !== 'Calendar' ? (
                  <div className="flex items-center">
                    <FileTextOutlined className="mr-2 opacity-70" />
                    <strong className="flex-1 pr-6 whitespace-normal line-clamp-2">
                      <Link to={`/deal_details/${record.data.id}`}>{record.data.subject || record.data.name}</Link>
                    </strong>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Tooltip title="Calendar Deal">
                      <StarFilled className="mr-2 opacity-70" />
                    </Tooltip>
                    <strong className="flex-1 pr-6 whitespace-normal line-clamp-2">
                      <Link to={`/calendar_deals/${record.data.id}`}>{record.data.subject || '--'}</Link>
                    </strong>
                  </div>
                )}
              </div>
            );
          } else {
            return (
              <Row align="middle" className="items-center">
                <Col span={22} className="whitespace-normal pr-3 flex-1 line-clamp-2">
                  {value}
                </Col>
                {mode == GanttChartMode.OWN_CALENDER ? (
                  <Button
                    type="link"
                    size="small"
                    className="add-task-icon bg-blue-500 flex items-center justify-center w-6 h-6 rounded-s-sm hover:bg-blue-400"
                    icon={<EditOutlined style={{ color: 'white' }} />}
                    onClick={() => {
                      handleEditTask(record.data?.parentId, record?.data.id, record.data);
                    }}
                  ></Button>
                ) : null}
              </Row>
            );
          }
        },
        sortable(a: any, b: any) {
          const anRaw = getNameValue(a);
          const bnRaw = getNameValue(b);

          const an = String(anRaw).toLowerCase();
          const bn = String(bnRaw).toLowerCase();

          return an.localeCompare(bn);
        },
        filterable: {
          filterField: {
            type: 'text'
          },
          filterFn: ({ record, value }: any) => {
            const name = getNameValue(record);
            return name.includes(value);
          }
        }
      },
      {
        id: 'col-reseller',
        text: 'Reseller',
        field: 'reseller',
        autoWidth: true,
        hidden: true,
        align: 'center',
        renderer: ({ record }: any) => {
          const reseller = record?.data?.reseller;
          return reseller?.name || '-';
        },
        sortable(a: any, b: any) {
          const an = a?.data?.reseller?.name || '';
          const bn = b?.data?.reseller?.name || '';
          return an.localeCompare(bn);
        },
        filterable: {
          filterField: {
            type: 'text'
          },
          filterFn: ({ record, value }: any) => {
            const reseller = record?.data?.reseller?.name || '';
            return reseller.includes(value);
          }
        }
      },
      {
        id: 'col-proposals',
        text: 'Proposals',
        field: 'proposals',
        width: 100,
        align: 'center',
        htmlEncode: false,
        renderer: ({ record }: any) => {
          const proposals = record?.data?.proposals;
          if (!record.isLeaf || !proposals?.length) {
            return '-';
          }
          return <RenewalProposalsPopover proposals={proposals} title={getNameValue(record)} />;
        },
        sortable(a: any, b: any) {
          return (a?.data?.proposals?.length || 0) - (b?.data?.proposals?.length || 0);
        },
        filterable: {
          // Combo filter on the proposal count (combo avoids Bryntum's operator picker).
          filterField: {
            type: 'combo',
            editable: false,
            items: [
              { text: '0', value: '0' },
              { text: '1', value: '1' },
              { text: '2', value: '2' },
              { text: '3', value: '3' },
              { text: '≥ 4', value: '>=4' }
            ]
          },
          filterFn: ({ record, value }: any) => {
            if (!value) return true;
            if (!record.isLeaf) return false;
            const count = record?.data?.proposals?.length || 0;
            return value === '>=4' ? count >= 4 : count === Number(value);
          }
        }
      },
      /* TEMP DISABLED: aggregated TCV column — not shown
      {
        id: 'col-tcvAgg',
        type: 'aggregate',
        text: 'TCV<br><span style="font-size:0.8em">(aggregated)</span>',
        field: 'tcv',
        width: 110,
        htmlEncode: false,
        htmlEncodeHeaderText: false,
        renderer: ({ record, value }: any) => {
          if (value !== undefined) {
            return record.isLeaf || record.data?.default_deal_tasks
              ? FormatCurrency(value, 0)
              : `<b>${FormatCurrency(value, 0)}</b>`;
          } else {
            return '';
          }
        }
      },
      */
      {
        id: 'col-renewal',
        type: 'date',
        filterType: 'text',
        text: 'Renewal',
        field: 'endDate',
        align: 'center',
        htmlEncode: false,
        width: 90,
        renderer: ({ record, value }: any) => {
          if (record.isLeaf && value) {
            return value && value < 0 ? `<i>Expired</i>` : moment(value).format('l');
          }
          return '-';
        },
        filterable: {
          filterField: {
            type: 'combo',
            editable: false,
            items: [
              { text: '3 months', value: moment().add(3, 'months').format('MM/DD/YYYY') },
              { text: '6 months', value: moment().add(6, 'months').format('MM/DD/YYYY') },
              { text: '12 months', value: moment().add(12, 'months').format('MM/DD/YYYY') },
              {
                text: `End of ${moment().format('Y')}`,
                value: moment(`${moment().get('year')}/12/31`).format('MM/DD/YYYY')
              }
            ]
          },
          filterFn: ({ record, value }: any) => {
            return moment(value).isAfter(record.data.endDate);
          }
        }
      },
      {
        id: 'col-noticePeriod',
        text: 'Notice<br>Period',
        field: 'notice_period',
        align: 'center',
        htmlEncode: false,
        htmlEncodeHeaderText: false,
        width: 90,
        renderer: ({ record }: any) => {
          if (record.isLeaf) {
            const noticePeriod = record.get('notice_period');
            return noticePeriod != null ? `${noticePeriod} days` : '-';
          }
          return '-';
        },
        filterable: true
      },
      {
        id: 'col-fmvReportLink',
        text: 'Vantage<br>Report',
        field: 'fmvReportLink',
        align: 'center',
        htmlEncode: false,
        htmlEncodeHeaderText: false,
        width: 80,
        renderer: ({ record }: any) => {
          const fmvReportLink = record.get('fmvReportLink');
          return fmvReportLink ? (
            <Tooltip title="Download Vantage Report">
              <Link target="_blank" to={fmvReportLink}>
                {<DownloadOutlined style={{ fontSize: 18 }} />}
              </Link>
            </Tooltip>
          ) : (
            '-'
          );
        },
        filterable: {
          filterField: {
            type: 'combo',
            editable: false,
            items: [
              { text: 'Has Report', value: 'Has Report' },
              { text: 'No Report', value: 'No Report' }
            ]
          },
          filterFn: ({ record, value }: any) => {
            return record.get('fmvReportLink') ? value === 'Has Report' : value === 'No Report';
          }
        }
      },
      {
        // v2 (early-access) replacement for 'col-fmvReportLink' — the deal layer has no
        // fmvReportLink, so the legacy single-link column is dead there. Shows the count of
        // the deal's ACTIVE deliverables; the popover lazy-fetches the rows on open.
        id: 'col-reports',
        text: 'Report(s)',
        field: 'deliverablesCount',
        align: 'center',
        htmlEncode: false,
        htmlEncodeHeaderText: false,
        width: 100,
        renderer: ({ record }: any) => {
          if (!record.isLeaf) return '-';
          // Load-bearing: group / non-deal rows carry no deal identifier, and the cell's fetch is
          // keyed by one. (Legacy 'Calendar' contract nodes DO have an id — see
          // `buildCalendarContractNode` — they're excluded by `deliverablesCount: 0` instead.)
          const dealId = record?.data?.id;
          if (dealId == null) return '-';
          return <CalendarReportsCell dealId={String(dealId)} count={record?.data?.deliverablesCount} />;
        },
        sortable(a: any, b: any) {
          return (a?.data?.deliverablesCount || 0) - (b?.data?.deliverablesCount || 0);
        },
        filterable: {
          // Combo filter on the report count (combo avoids Bryntum's operator picker),
          // mirroring 'col-proposals'.
          filterField: {
            type: 'combo',
            editable: false,
            items: [
              { text: '0', value: '0' },
              { text: '1', value: '1' },
              { text: '2', value: '2' },
              { text: '3', value: '3' },
              { text: '≥ 4', value: '>=4' }
            ]
          },
          filterFn: ({ record, value }: any) => {
            if (!value) return true;
            if (!record.isLeaf) return false;
            const count = record?.data?.deliverablesCount || 0;
            return value === '>=4' ? count >= 4 : count === Number(value);
          }
        }
      },
      {
        id: 'col-primaryContact',
        text: 'Client<br>Contact',
        field: 'primaryContact',
        autoWidth: true,
        align: 'center',
        htmlEncodeHeaderText: false,
        htmlEncode: false,
        minWidth: 80,
        sortable(a: any, b: any) {
          const an = getPrimaryContacts(a).join(',');
          const bn = getPrimaryContacts(b).join(',');
          return FieldSorter(an, bn);
        },
        renderer: ({ record }: any) => {
          // Check if record.data exists to prevent undefined access errors
          if (!record?.data) {
            return <></>;
          }

          if (record.data.recordType === SUPPLIER_DEAL || record.data.recordType === DEAL) {
            if (record.data.clientContact?.Name) {
              const fullName = record.data.clientContact?.Name;
              return (
                <Tooltip title={renderToolTipForClientContact('Deal Owner', [fullName])}>
                  {renderClientContactAvatars(fullName, 'custom-avatar')}
                </Tooltip>
              );
            }
          } else if (record.data.recordType === TASK) {
            const names = record.data.assignment?.map((item: any) => item.users.name);
            if (names && names.length > 0) {
              const additionalNamesCount = names.length - 1;
              return (
                <Tooltip title={renderToolTipForClientContact('Task Assignees', names)}>
                  <Badge
                    count={additionalNamesCount > 0 ? `+${additionalNamesCount}` : null}
                    offset={[0, 30]}
                    style={{ backgroundColor: '#152d51', borderRadius: '50%', marginTop: '26px', padding: '0px' }}
                  >
                    {renderClientContactAvatars(names[0], 'custom-task-avatar')}
                  </Badge>
                </Tooltip>
              );
            }
          } else {
            return <></>;
          }
        },
        filterable: {
          filterField: {
            type: 'text'
          },
          filterFn: ({ record, value }: any) => {
            const primaryContacts = getPrimaryContacts(record);
            return primaryContacts.some((contact: string) => contact.includes(value));
          }
        }
      },
      {
        id: 'col-status',
        text: 'Submitted',
        field: 'status',
        autoWidth: true,
        align: 'center',
        renderer: ({ record, value }: any) =>
          record.isLeaf && value ? (
            value === 'Calendar' ? (
              <CheckCircleOutlined style={{ color: '#ccc', fontSize: 20 }} />
            ) : (
              <CheckCircleOutlined style={{ color: 'green', fontWeight: 500, fontSize: 20 }} />
            )
          ) : (
            '-'
          ),
        filterable: {
          filterField: {
            type: 'combo',
            editable: false,
            items: [
              { text: ContractStatus.CALENDAR, value: ContractStatus.CALENDAR },
              { text: ContractStatus.SUBMITTED, value: ContractStatus.SUBMITTED }
            ]
          },
          filterFn: ({ record, value }: any) => {
            if (!value) return true;
            const status = record.data?.status;
            // The column renders a binary affordance — 'Calendar' placeholders vs.
            // every real (submitted) deal. Match that, not exact status strings:
            // deal_status spans many values (Submitted, In Progress, Findings Provided…).
            if (!record.isLeaf || !status) return false;
            return value === ContractStatus.CALENDAR
              ? status === ContractStatus.CALENDAR
              : status !== ContractStatus.CALENDAR;
          }
        }
      },
      {
        id: 'col-categories',
        hidden: true,
        text: 'Categories',
        field: 'categories',
        autoWidth: true,
        renderer: ({ record }: any) => {
          {
            const categories = record?.data?.contract_category || [];
            const categoryNames = categories
              .reduce((names: string[], category: any) => {
                names.push(category.category_root_name);
                names.push(category.category_prime_name);
                names.push(...(category.category_second?.map((s: any) => s.category_second_name) || []));
                return names;
              }, [])
              .filter((name: string) => !!name);

            if (!Array.isArray(categoryNames) || categoryNames.length === 0) return '-';

            const renderToolTipForCategories = (title: string, categories: Array<any>): React.ReactElement => (
              <div style={{ textAlign: 'center' }}>
                <span className="deal-owner-text">
                  {title}
                  <hr className="hr-no-space" />
                </span>
                {categories.map((category, index: number) => (
                  <span className="deal-owner-names" key={`category-${index}`}>
                    {category}
                    <br />
                  </span>
                ))}
              </div>
            );

            return (
              <Tooltip title={renderToolTipForCategories('Categories', categoryNames)}>
                <div>
                  <Pebble name={categoryNames[0]} extra={categoryNames.length - 1} />
                </div>
              </Tooltip>
            );
          }
        },
        filterable: {
          filterFn: ({ record, value }: any) => {
            const categories = record?.data?.contract_category || [];
            const categoryNames = categories
              .reduce((names: string[], category: any) => {
                names.push(category.category_root_name);
                names.push(category.category_prime_name);
                names.push(...(category.category_second?.map((s: any) => s.category_second_name) || []));
                return names;
              }, [])
              .filter((name: string) => !!name);
            return categoryNames.some((name: string) => name.includes(value));
          }
        },
        sortable: false
      },
      {
        id: 'col-tags',
        hidden: true,
        filterType: 'text',
        text: 'Tags',
        field: 'tags',
        autoWidth: true,
        renderer: ({ record }: any) => {
          {
            // Check if record.data exists to prevent undefined access errors
            const tags = record?.data?.tags;
            if (!Array.isArray(tags) || tags.length === 0) return '-';

            const renderToolTipForTags = (title: string, tags: Array<any>): React.ReactElement => (
              <div style={{ textAlign: 'center' }}>
                <span className="deal-owner-text">
                  {title}
                  <hr className="hr-no-space" />
                </span>
                {tags.map((tag: { id: number; name: string; color: string }, index: number) => (
                  <span className="deal-owner-names" key={index}>
                    {tag.name}
                    <br />
                  </span>
                ))}
              </div>
            );

            return (
              <Tooltip title={renderToolTipForTags('Tags', tags)}>
                <div>
                  <Pebble color={tags[0].color} name={tags[0].name} extra={tags.length - 1} />
                </div>
              </Tooltip>
            );
          }
        },
        filterable: {
          filterFn: ({ record, value }: any) => {
            const tags = record?.data?.tags || [];
            return tags.some((tag: any) => tag.name.includes(value));
          }
        },
        sortable: false
      },
      {
        id: 'col-alert',
        text: 'Alert',
        filterType: 'text',
        field: 'endDate',
        align: 'center',
        width: 60,
        renderer: ({ record, value }: any) =>
          record.isLeaf ? (
            value && moment(value) > moment() ? (
              <RenewalAlertsModal
                contract_id={record.originalData.id}
                onSave={(items) => {
                  // Filter out deleted/ignored notifications so bell icon reflects active notifications only
                  const activeItems = items.filter((item) => item.action !== 'delete' && item.action !== 'ignore');
                  record.set('notification_queue', activeItems);
                }}
              >
                {(record.data?.notification_queue?.length > 0 && <BellFilled style={{ color: 'green' }} />) || (
                  <BellOutlined style={{ color: '#aaa' }} />
                )}
              </RenewalAlertsModal>
            ) : (
              '-'
            )
          ) : (
            ''
          ),
        filterable: false,
        sortable: false
      },
      {
        id: 'col-fye',
        text: 'Fiscal<br>Year End',
        field: 'fiscal_year_end',
        autoWidth: true,
        htmlEncode: false,
        align: 'center',
        htmlEncodeHeaderText: false,
        renderer: ({ record }: any) =>
          record.data?.fiscal_year_end ? moment(record.data.fiscal_year_end, 'MM-DD', true).format('MMM Do') : '-',
        filterable: {
          filterField: {
            type: 'text'
          },
          filterFn: ({ record, value }: any) => {
            const fiscalYearEnd = record?.data?.fiscal_year_end;
            const fiscalYearEndFormatted = fiscalYearEnd ? moment(fiscalYearEnd, 'MM-DD', true).format('MMM Do') : '';
            return fiscalYearEndFormatted.includes(value);
          }
        }
      },
      {
        id: 'col-vendorComplexity',
        type: 'number',
        filterType: 'text',
        text: 'Vendor<br>Complexity',
        field: 'vendorComplexity',
        align: 'center',
        htmlEncodeHeaderText: false,
        width: 100,
        sortable(a: any, b: any) {
          const obj1 = getSupplierDynamicsObj(a, DYNAMIC_COLUMNS.VENDOR_COMPLEXITY);
          const obj2 = getSupplierDynamicsObj(b, DYNAMIC_COLUMNS.VENDOR_COMPLEXITY);
          const av = obj1?.value !== null && obj1?.value !== undefined ? Number(obj1?.value) : Number.POSITIVE_INFINITY;
          const bv = obj2?.value !== null && obj2?.value !== undefined ? Number(obj2?.value) : Number.POSITIVE_INFINITY;
          return av - bv;
        },
        renderer: ({ record }: any) => {
          // Check if record.data exists to prevent undefined access errors
          if (!record?.data) {
            return <></>;
          }

          if (record.data.recordType === SUPPLIER_DEAL || record.data.recordType === SUPPLIER) {
            const vendorComplexity = getSupplierDynamicsObj(record, DYNAMIC_COLUMNS.VENDOR_COMPLEXITY);
            if (vendorComplexity) {
              const name = vendorComplexity.dealDynamic.name;
              const value = PURCHASE_CHARACTERISTICS_VALUE[vendorComplexity.value - 1];
              const avatarColor = PURCHASE_CHARACTERISTICS_THREE_COLORS[vendorComplexity.value - 1];
              const avatarValue = value.charAt(0) || '';
              const tooltipContent = vendorComplexity?.dealDynamic?.scoreTooltips?.[vendorComplexity.value - 1];

              return (
                <SupplierDynamicsPopover
                  name={name}
                  avatarColor={avatarColor}
                  value={value}
                  avatarValue={avatarValue}
                  description={tooltipContent}
                ></SupplierDynamicsPopover>
              );
            } else {
              return <></>;
            }
          }
        },
        filterable: {
          filterField: {
            type: 'combo', // Bryntum combo supports multiSelect
            editable: false,
            items: [
              { text: PURCHASE_CHARACTERISTICS_VALUE[0], value: PURCHASE_CHARACTERISTICS_VALUE[0] },
              { text: PURCHASE_CHARACTERISTICS_VALUE[1], value: PURCHASE_CHARACTERISTICS_VALUE[1] },
              { text: PURCHASE_CHARACTERISTICS_VALUE[2], value: PURCHASE_CHARACTERISTICS_VALUE[2] }
            ]
          },
          filterFn: ({ record, value }: any) => {
            let filter = false;
            if (record?.data && (record.data.recordType === SUPPLIER_DEAL || record.data.recordType === SUPPLIER)) {
              const vendorComplexity = getSupplierDynamicsObj(record, DYNAMIC_COLUMNS.VENDOR_COMPLEXITY);
              if (vendorComplexity) {
                const v = PURCHASE_CHARACTERISTICS_VALUE[vendorComplexity.value - 1];
                filter = v === value;
              }
            }
            return filter;
          }
        }
      },
      {
        id: 'col-pricingFlexibility',
        filterType: 'text',
        text: 'Pricing<br>Flexibility',
        field: 'pricingFlexibility',
        htmlEncodeHeaderText: false,
        align: 'center',
        width: 110,
        sortable(a: any, b: any) {
          const obj1 = getSupplierDynamicsObj(a, DYNAMIC_COLUMNS.PRICING_FLEXIBILITY);
          const obj2 = getSupplierDynamicsObj(b, DYNAMIC_COLUMNS.PRICING_FLEXIBILITY);
          return obj1?.value - obj2?.value;
        },
        renderer: ({ record }: any) => {
          if ((record?.data && record.data.recordType === SUPPLIER_DEAL) || record.data.recordType === SUPPLIER) {
            const pricingFlexibility = getSupplierDynamicsObj(record, DYNAMIC_COLUMNS.PRICING_FLEXIBILITY);
            if (pricingFlexibility) {
              const name = pricingFlexibility.dealDynamic.name;
              const value = PURCHASE_CHARACTERISTICS_VALUE[pricingFlexibility.value - 1];
              const avatarColor = PURCHASE_CHARACTERISTICS_THREE_COLORS[pricingFlexibility.value - 1];
              const avatarValue = value.charAt(0) || '';
              const tooltipContent = pricingFlexibility?.dealDynamic?.scoreTooltips?.[pricingFlexibility.value - 1];
              return (
                <SupplierDynamicsPopover
                  name={name}
                  avatarColor={avatarColor}
                  value={value}
                  avatarValue={avatarValue}
                  description={tooltipContent}
                ></SupplierDynamicsPopover>
              );
            } else {
              return <></>;
            }
          }
        },
        filterable: {
          filterField: {
            type: 'combo', // Bryntum combo supports multiSelect
            editable: false,
            items: [
              { text: PURCHASE_CHARACTERISTICS_VALUE[0], value: PURCHASE_CHARACTERISTICS_VALUE[0] },
              { text: PURCHASE_CHARACTERISTICS_VALUE[1], value: PURCHASE_CHARACTERISTICS_VALUE[1] },
              { text: PURCHASE_CHARACTERISTICS_VALUE[2], value: PURCHASE_CHARACTERISTICS_VALUE[2] }
            ]
          },
          filterFn: ({ record, value }: any) => {
            let filter = false;
            if ((record?.data && record.data.recordType === SUPPLIER_DEAL) || record.data.recordType === SUPPLIER) {
              const pricingFlexibility = getSupplierDynamicsObj(record, DYNAMIC_COLUMNS.PRICING_FLEXIBILITY);
              if (pricingFlexibility) {
                const v = PURCHASE_CHARACTERISTICS_VALUE[pricingFlexibility.value - 1];
                filter = v === value;
              }
            }
            return filter;
          }
        }
      },
      {
        id: 'col-dealSizeTrend',
        type: 'number',
        text: 'Deal Size<br>Trend',
        field: 'dealSizeTrend',
        htmlEncodeHeaderText: false,
        align: 'center',
        width: 110,
        sortable(a: any, b: any) {
          const obj1 = getSupplierDynamicsObj(a, DYNAMIC_COLUMNS.DEAL_SIZE_TREND);
          const obj2 = getSupplierDynamicsObj(b, DYNAMIC_COLUMNS.DEAL_SIZE_TREND);
          const av = obj1?.value !== null && obj1?.value !== undefined ? Number(obj1?.value) : Number.POSITIVE_INFINITY;
          const bv = obj2?.value !== null && obj2?.value !== undefined ? Number(obj2?.value) : Number.POSITIVE_INFINITY;
          return av - bv;
        },
        renderer: ({ record }: any) => {
          // Check if record.data exists to prevent undefined access errors
          if (!record?.data) {
            return <></>;
          }

          if (record.data.recordType === SUPPLIER_DEAL || record.data.recordType === SUPPLIER) {
            const dealSizeTrend = getSupplierDynamicsObj(record, DYNAMIC_COLUMNS.DEAL_SIZE_TREND);
            if (dealSizeTrend && dealSizeTrend.value !== null) {
              let tooltipIndex;
              let value;
              if (dealSizeTrend.value < 0) {
                tooltipIndex = 0;
                value = DEAL_SIZE_TREND_CHARACTERISTICS.TRENDING_DOWNWARDS_TEXT;
              } else if (dealSizeTrend.value == 0) {
                tooltipIndex = 1;
                value = DEAL_SIZE_TREND_CHARACTERISTICS.STABLE_TEXT;
              } else {
                tooltipIndex = 2;
                value = DEAL_SIZE_TREND_CHARACTERISTICS.TRENDING_UPWARDS_TEXT;
              }
              const tooltipContent = dealSizeTrend?.dealDynamic?.scoreTooltips?.[tooltipIndex];
              const percentageValue = `${Math.round(dealSizeTrend.value * 100)}%`;
              const name = dealSizeTrend.dealDynamic.name;
              const avatarColor = getDealSizeTrendColor(parseFloat(percentageValue));

              return (
                <SupplierDynamicsPopover
                  name={name}
                  avatarColor={avatarColor}
                  value={value}
                  avatarValue={percentageValue}
                  description={tooltipContent}
                  width={50}
                ></SupplierDynamicsPopover>
              );
            } else {
              return <></>;
            }
          }
        },
        filterable: {
          filterFn: ({ record, value, operator }: any) => {
            // Convert value to percentage
            const valuePercentage = Array.isArray(value)
              ? value.map((v: any) => (v?.text ? Number(v?.text) / 100 : v / 100))
              : value / 100;

            // Switch case to handle different operators
            let filter = false;
            if (record?.data && (record.data.recordType === SUPPLIER_DEAL || record.data.recordType === SUPPLIER)) {
              const dealSizeTrend = getSupplierDynamicsObj(record, DYNAMIC_COLUMNS.DEAL_SIZE_TREND);
              switch (operator) {
                case 'empty':
                  filter = dealSizeTrend?.value === null || dealSizeTrend?.value === undefined;
                  break;
                case 'notEmpty':
                  filter = dealSizeTrend?.value !== null && dealSizeTrend?.value !== undefined;
                  break;
              }
              if (dealSizeTrend?.value !== null && dealSizeTrend?.value !== undefined) {
                switch (operator) {
                  case '>':
                    filter = dealSizeTrend?.value > valuePercentage;
                    break;
                  case '<':
                    filter = dealSizeTrend?.value < valuePercentage;
                    break;
                  case '=':
                    filter = dealSizeTrend?.value == valuePercentage;
                    break;
                  case '!=':
                    filter = dealSizeTrend?.value != valuePercentage;
                    break;
                  case '>=':
                    filter = dealSizeTrend?.value >= valuePercentage;
                    break;
                  case '<=':
                    filter = dealSizeTrend?.value <= valuePercentage;
                    break;
                  case 'between':
                    filter =
                      Array.isArray(valuePercentage) &&
                      dealSizeTrend?.value >= valuePercentage[0] &&
                      dealSizeTrend?.value <= valuePercentage[1];
                    break;
                  case 'notBetween':
                    filter =
                      Array.isArray(valuePercentage) &&
                      (dealSizeTrend?.value < valuePercentage[0] || dealSizeTrend?.value > valuePercentage[1]);
                    break;
                  case 'isIncludedIn':
                    filter = Array.isArray(valuePercentage) && valuePercentage.includes(dealSizeTrend?.value);
                    break;
                  case 'isNotIncludedIn':
                    filter = Array.isArray(valuePercentage) && !valuePercentage.includes(dealSizeTrend?.value);
                    break;
                }
              }
            }

            return filter;
          }
        }
      }
      // Custom fields
    ];

    // Render order is driven by GANTT_COLUMN_ORDER (the declared source of truth), not by the
    // physical position of objects in `ganttChartColumns` — see that const's JSDoc. The
    // allow-list + order composition lives in the exported `resolveRenderedColumnIds` so this
    // wiring and its unit tests observe the exact same function.
    const columnsById = new Map(
      (ganttChartColumns as Partial<GridColumnConfig>[]).map((column: any) => [String(column.id), column])
    );
    const columns = resolveRenderedColumnIds(mode, ACLCheck(['read:early_access']))
      .map((columnId) => columnsById.get(columnId))
      .filter((column): column is Partial<GridColumnConfig> => column != null);

    return {
      columns,
      viewPreset: 'weekAndDayLetter',
      barMargin: 8,
      autoHeight: true,
      readOnly: false,
      taskRenderer: taskRenderer as any
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);
};

const projectConfig: Partial<ProjectModelConfig> = {
  calendar: 'general',
  // startDate: DateHelper.add(DateHelper.clearTime(new Date()), 0, 'day'),
  hoursPerDay: 24,
  daysPerWeek: 5,
  daysPerMonth: 20,
  autoLoad: true,
  taskModelClass: MyTaskModel,
  // This config enables response validation and dumping of found errors to the browser console.
  // It's meant to be used as a development stage helper only so please set it to false for production systems.
  validateResponse: true
};

const customTaskTooltip = ({ taskRecord }: any): any => {
  // Check if taskRecord.data exists to prevent undefined access errors
  if (!taskRecord?.data) {
    return (
      <div className="custom-tooltip">
        <h4 className={'font-bold'}>No data available</h4>
      </div>
    );
  }

  return (
    <div className="custom-tooltip">
      {taskRecord.data.orgName && <h4 className={'text-gray-500'}>{taskRecord.data.orgName}</h4>}
      <h4 className={'font-bold'}>{taskRecord.data.subject || taskRecord.data.name}</h4>
      <hr className={'border-gray-100'} />
      {/* TEMP DISABLED: tooltip TCV — same rationale as col-tcvAgg (unreliable field).
      <Row>
        <Col span={10} className={'label'}>
          Total Value:
        </Col>
        <Col span={14}>{taskRecord.data.tcv ? FormatCurrency(taskRecord.data.tcv) : '--'}</Col>
      </Row>
      */}
      <Row>
        <Col span={10} className={'label'}>
          Start Date:
        </Col>
        <Col span={14}>{moment(taskRecord.data.startDate).format('LL') || '--'}</Col>
      </Row>
      <Row>
        <Col span={10} className={'label'}>
          End Date:
        </Col>
        <Col span={14}>{moment(taskRecord.data.endDate).format('LL') || '--'}</Col>
      </Row>
    </div>
  );
};

export { useGanttConfig, projectConfig, customTaskTooltip };
